import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client/react';
import { Tooltip } from '@progress/kendo-react-tooltip';
import ConfirmationDialog from '@components-clean/core/confirmation-dialog';
import { InfoField } from '@components-clean/core/info-field';
import {
    ExtendedCompleteRiskFields,
    ExtendedCompleteRiskFieldsControl,
} from '@pages-clean/risk-assessment/risk-log/constants';
import { RiskLogContext } from '@pages-clean/risk-assessment/risk-log/interfaces/context';
import { assignControls, assignControlsVariables } from '@queries/generated/risk-assessment.generated';
import { GET_STUDY_CONTROLS } from '@queries/legacy-risk-assessment';
import { ASSIGN_CONTROLS, GET_COMPLETE_RISKS_RISK_LOG } from '@queries/risk-assessment';

interface UnassignControlProps {
    onClose: () => void;
    onSubmit: (result: string) => void;
    risk: ExtendedCompleteRiskFields;
    control: ExtendedCompleteRiskFieldsControl;
}

const UnassignControlDialog = ({ onClose, onSubmit, risk, control }: UnassignControlProps) => {
    const { t } = useTranslation('ram', { keyPrefix: 'riskLog.dialogs.unassignControl' });
    const { current } = useContext(RiskLogContext);

    const [assignControlsMutation] = useMutation<assignControls, assignControlsVariables>(ASSIGN_CONTROLS);

    const handleSubmit = async () => {
        assignControlsMutation({
            variables: {
                id: risk.id,
                controlIds: (risk.controlIds ?? []).filter((id) => id !== control.id),
            },
            refetchQueries: [
                {
                    query: GET_COMPLETE_RISKS_RISK_LOG,
                    variables: { studyId: current?.study?.id },
                },
                {
                    query: GET_STUDY_CONTROLS,
                    variables: { studyId: current?.study?.id },
                },
            ],
        })
            .then(async (response) => {
                if (!response?.data || !response?.data?.assignControls) {
                    onSubmit('error');
                    return;
                }
                onSubmit('success');
            })
            .catch(() => {
                onSubmit('error');
            });
    };

    return (
        <ConfirmationDialog
            title={t('title')}
            dataTestId="unassign-control-confirmation-dialog"
            onClose={() => onClose()}
            onSubmit={() => handleSubmit()}
        >
            <div>
                <InfoField
                    type="Info"
                    message={
                        <div style={{ width: '438px', margin: '0 0' }}>
                            <div className="k-font-medium">{t('riskStatement')}</div>
                            <div>{risk?.statement?.slice(0, 600)}</div>
                            <br />
                            <div className="k-d-flex k-align-items-center">
                                <div className="k-font-medium">{t('controlDescription')}</div>
                            </div>
                            <Tooltip anchorElement="target" position="auto" parentTitle style={{ maxWidth: '600px' }}>
                                <div title={control?.description ?? ''}>
                                    {(() => {
                                        const desc = control?.description ?? '';
                                        return desc.length > 600 ? `${desc.slice(0, 600)}...` : desc;
                                    })()}
                                </div>
                            </Tooltip>
                        </div>
                    }
                />
                <div style={{ textAlign: 'center', marginTop: '30px', marginBottom: '18px' }}>{t('message')}</div>
            </div>
        </ConfirmationDialog>
    );
};

export default UnassignControlDialog;
